import { Fiche, FicheContainerContext } from '@mairistem/fiche';
import _ from 'lodash';
import React from 'react';
import FicheErrorBoundary from '@/components/Autre/FicheErrorBoundary';
import type { RegieType } from '@/types';
import { regiePanelValidators } from '@/utils/validators/fiche';
import EncaisseArretes from './Panel/EncaisseArretes';
import Identification from './Panel/Identification';
import ModesReglement from './Panel/ModesReglement';
import PiecesJointes from './Panel/PiecesJointes';
import PosteComptable from './Panel/PosteComptable';
import Produits from './Panel/Produits';
import Recus from './Panel/Recus';
import RegieProlongee from './Panel/RegieProlongee';
import Regisseurs from './Panel/Regisseurs';

interface ConstitutionRegieFicheProps {
  loading?: boolean;
  errors?: { id: string; message: string }[];
  regie?: RegieType;
  onChange?: (value: Record<string, unknown>, shouldChangeStatus?: boolean) => void;
}

const ConstitutionRegieFiche = (props: ConstitutionRegieFicheProps) => {
  const { loading, errors, regie, onChange } = props;

  const {
    identifiant,
    adresse,
    collectivite,
    horairesOuverture,
    service,
    titre,
    codeInterne,
    ecole,
    autonome,
    // Informations de contact :
    mail,
    telephone,
    website,
    // Régisseurs
    regisseurConnexion,
    regisseurs,
    // Poste comptable et DFT :
    tresorerie,
    depotFondTresor,
    protocoleImpaye,
    comptabilite,
    seuilRecouvrement,
    // Reçus
    titreRecuImprime,
    envoiRecuParMail,
    recuDetailleProduit,
    htmlMailRecu,
    ribDepot,
    intituleCompteDepot,
    // Encaisse :
    fondCaisse,
    encaisseMaximum,
    encaisseNumeraireMaximum,
    periodiciteVersementEncaisseGlobale,
    // Arrêtés de régie :
    periodiciteArrete,
    dateFinDernierArrete,
    // Relances (panel « Relances ») :
    regieProlongee,
    joursEncaissementSupplementaires,
    // Relances après date limite de paiement (historique)
    envoiRelanceParMail,
    seuilRelance,
    texteRelance,
    htmlMailRelance,
    // Relances avant date limite de paiement
    envoiRelanceAvantEcheanceParMail,
    seuilRelanceAvantEcheance,
    texteRelanceAvantEcheance,
    htmlMailRelanceAvantEcheance,
    // Informations qui ne sont pas dans le JSON de l'API mais rajoutés au fur et à mesure
    // en création ou modification
    logo,
    modesReglements,
    addedDocuments,
  } = regie || {};

  const { status, handleValidate } = React.useContext(FicheContainerContext);

  // Re-valide au changement de données pour effacer les erreurs au fur et à mesure
  React.useEffect(() => {
    if (!_.isEmpty(errors) && handleValidate) {
      handleValidate();
    }
  }, [regie, handleValidate]);

  const handleChange = React.useCallback(
    (e, shouldChangeStatus: boolean = true) => {
      // Si on a RAZ l'adresse
      if (
        e?.adresse &&
        _.isEmpty(e.adresse.numeroCodePostal) &&
        _.isEmpty(e.adresse.lettre) &&
        _.isEmpty(e.adresse.numeroVoirie) &&
        _.isEmpty(e.adresse.libellePays) &&
        _.isEmpty(e.adresse.libelleVoie)
      ) {
        onChange?.({ ...e, adresse: null }, shouldChangeStatus);
      } else {
        onChange?.(e, shouldChangeStatus);
      }
    },
    [onChange],
  );

  const identification = React.useMemo(
    () => (
      <Fiche.Panel
        key="identification"
        id="identification"
        title="IDENTIFICATION"
        open
        error={regiePanelValidators.identificationHasErrors(errors)}
      >
        <Identification
          identifiant={identifiant}
          errors={errors}
          adresse={adresse}
          collectivite={collectivite}
          horairesOuverture={horairesOuverture}
          service={service}
          titre={titre}
          codeInterne={codeInterne}
          autonome={autonome}
          mail={mail}
          telephone={telephone}
          website={website}
          logo={logo}
          onChange={handleChange}
        />
      </Fiche.Panel>
    ),
    [
      errors,
      adresse,
      autonome,
      collectivite,
      codeInterne,
      logo,
      handleChange,
      horairesOuverture,
      identifiant,
      mail,
      service,
      telephone,
      titre,
      website,
    ],
  );

  const panelRegisseur = React.useMemo(
    () => (
      <Fiche.Panel
        key="regisseurs"
        id="regisseurs"
        title="RÉGISSEURS"
        open
        error={regiePanelValidators.regisseursHasErrors(errors)}
      >
        <Regisseurs
          identifiant={identifiant}
          regisseurs={regisseurs}
          regisseurConnexion={regisseurConnexion}
          onChange={handleChange}
        />
      </Fiche.Panel>
    ),
    [errors, identifiant, regisseurs, regisseurConnexion, handleChange],
  );

  const posteComptable = React.useMemo(
    () => (
      <Fiche.Panel
        key="posteComptable"
        id="posteComptable"
        title="POSTE COMPTABLE"
        open
        error={regiePanelValidators.posteComptableHasErrors(errors)}
      >
        <PosteComptable
          identifiant={identifiant}
          errors={errors}
          onChange={handleChange}
          tresorerie={tresorerie}
          depotFondTresor={depotFondTresor}
          ribDepot={ribDepot}
          intituleCompteDepot={intituleCompteDepot}
          protocoleImpaye={protocoleImpaye}
          comptabilite={comptabilite}
          seuilRecouvrement={seuilRecouvrement}
        />
      </Fiche.Panel>
    ),
    [
      errors,
      depotFondTresor,
      ribDepot,
      intituleCompteDepot,
      handleChange,
      identifiant,
      tresorerie,
      protocoleImpaye,
      comptabilite,
      seuilRecouvrement,
    ],
  );

  const produitsModesReglement = React.useMemo(
    () => (
      <Fiche.Panel key="produits" id="produits" title="Produits" open>
        <Produits identifiant={identifiant} autonome={autonome} />
      </Fiche.Panel>
    ),
    [identifiant, autonome],
  );

  const modesReglementsPanel = React.useMemo(
    () => (
      <Fiche.Panel
        key="modesReglements"
        id="modesReglements"
        title="Modes de règlement"
        open
        error={regiePanelValidators.modesReglementHasErrors(errors)}
      >
        <ModesReglement
          identifiant={identifiant}
          onChange={handleChange}
          modesReglements={modesReglements}
          depotFondTresor={depotFondTresor}
        />
      </Fiche.Panel>
    ),
    [errors, handleChange, identifiant, modesReglements, depotFondTresor],
  );

  const encaisseArretes = React.useMemo(
    () => (
      <Fiche.Panel
        key="encaisseArretes"
        id="encaisseArretes"
        title="Encaisse - Arrêtés"
        open
        error={regiePanelValidators.encaisseArretesHasErrors(errors)}
      >
        <EncaisseArretes
          identifiant={identifiant}
          errors={errors}
          encaisseMaximum={encaisseMaximum}
          encaisseNumeraireMaximum={encaisseNumeraireMaximum}
          fondCaisse={fondCaisse}
          periodiciteArrete={periodiciteArrete}
          periodiciteVersementEncaisseGlobale={periodiciteVersementEncaisseGlobale}
          dateFinDernierArrete={dateFinDernierArrete}
          onChange={handleChange}
        />
      </Fiche.Panel>
    ),
    [
      errors,
      encaisseNumeraireMaximum,
      encaisseMaximum,
      fondCaisse,
      handleChange,
      identifiant,
      periodiciteArrete,
      periodiciteVersementEncaisseGlobale,
      dateFinDernierArrete,
    ],
  );

  const recus = React.useMemo(
    () => (
      <Fiche.Panel key="recus" id="recus" title="Reçus" open>
        <Recus
          identifiant={identifiant}
          onChange={handleChange}
          titreRecuImprime={titreRecuImprime}
          envoiRecuParMail={envoiRecuParMail}
          recuDetailleProduit={recuDetailleProduit}
          htmlMailRecu={htmlMailRecu}
          refresh={status?.updated}
        />
      </Fiche.Panel>
    ),
    [
      handleChange,
      identifiant,
      titreRecuImprime,
      envoiRecuParMail,
      recuDetailleProduit,
      htmlMailRecu,
      status?.updated,
    ],
  );

  const panelRegieProlongee = React.useMemo(
    () => (
      <Fiche.Panel
        key="regieProlongee"
        id="regieProlongee"
        title="Relances"
        open
        error={regiePanelValidators.regieProlongeeHasErrors(errors)}
      >
        <RegieProlongee
          identifiant={identifiant}
          errors={errors}
          onChange={handleChange}
          regieProlongee={regieProlongee}
          joursEncaissementSupplementaires={joursEncaissementSupplementaires}
          envoiRelanceParMail={envoiRelanceParMail}
          seuilRelance={seuilRelance}
          texteRelance={texteRelance}
          htmlMailRelance={htmlMailRelance}
          envoiRelanceAvantEcheanceParMail={envoiRelanceAvantEcheanceParMail}
          seuilRelanceAvantEcheance={seuilRelanceAvantEcheance}
          texteRelanceAvantEcheance={texteRelanceAvantEcheance}
          htmlMailRelanceAvantEcheance={htmlMailRelanceAvantEcheance}
          updated={status?.updated}
        />
      </Fiche.Panel>
    ),
    [
      errors,
      handleChange,
      identifiant,
      regieProlongee,
      joursEncaissementSupplementaires,
      envoiRelanceParMail,
      seuilRelance,
      texteRelance,
      htmlMailRelance,
      envoiRelanceAvantEcheanceParMail,
      seuilRelanceAvantEcheance,
      texteRelanceAvantEcheance,
      htmlMailRelanceAvantEcheance,
      status?.updated,
    ],
  );

  const panelPiecesJointes = React.useMemo(
    () => (
      <Fiche.Panel key="documents" id="documents" title="Documents" open>
        <PiecesJointes
          identifiant={identifiant}
          addedDocuments={addedDocuments}
          refresh={status?.updated}
          onChange={handleChange}
        />
      </Fiche.Panel>
    ),
    [identifiant, addedDocuments, status?.updated, handleChange],
  );

  const header = React.useMemo(() => {
    if (status?.created && !status?.updated) {
      return `Constitution d'une nouvelle régie de recettes${autonome ? ' (autonome)' : ''}`;
    }

    return `${titre || ''}${ecole ? ' (Régie test)' : ''}`;
  }, [autonome, ecole, status?.created, status?.updated, titre]);

  const withErrorBoundary = React.useCallback((content) => {
    if (process.env.NODE_ENV === 'development') {
      // TODO: évite la boucle infini de la redirection si une erreur apparaît
      // sur la fiche (fiche redirect > '_' puis router redirect > 'id')
      // Ca évite que le PC décolle pour la lune dès qu'on a une erreur d'import ou autre...
      // Mais idéalement faut le gérer pour la prod également
      return <FicheErrorBoundary>{content}</FicheErrorBoundary>;
    }

    return content;
  }, []);

  return withErrorBoundary(
    <Fiche
      key="ConstitutionRegieFiche"
      type="regie"
      header={header}
      loading={loading}
      errors={errors}
    >
      {identification}
      {panelRegisseur}
      {posteComptable}
      {produitsModesReglement}
      {modesReglementsPanel}
      {encaisseArretes}
      {recus}
      {/* Régie autonome : pas de relances */}
      {!autonome && panelRegieProlongee}
      {panelPiecesJointes}
    </Fiche>,
  );
};

export default ConstitutionRegieFiche;
